<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catMint" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-flag-checkered" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="hasData"
        x-small
        color="catMint"
        outlined
        class="ml-2 flex-shrink-0"
      >
        {{ goals.length }}
      </v-chip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar metas</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-flag-outline</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhuma meta registrada ainda.
        </p>
        <v-btn small color="catMint" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Adicionar meta
        </v-btn>
      </div>

      <!-- Lista de metas -->
      <div v-else class="goals-list">
        <div
          v-for="goal in goals"
          :key="goal.id"
          class="goal-row"
          @click="openDialog"
        >
          <div class="goal-main">
            <span class="goal-name">{{ goal.goal_name }}</span>
            <v-chip
              x-small
              :color="statusColor(goal.status)"
              text-color="white"
              class="ml-2 flex-shrink-0"
            >
              {{ goal.status }}
            </v-chip>
          </div>
          <div class="goal-sub">
            <span v-if="goal.goal_type">{{ goal.goal_type }}</span>
            <span v-if="hasTarget(goal)" class="ml-2">
              {{ goal.current_value ?? "—" }}/{{ goal.target_value }}
              {{ goal.unit }}
            </span>
          </div>
        </div>
      </div>
    </v-card-text>

    <!-- Histórico de metas (lista) — abre o GoalFormDialog por dentro -->
    <EditGoalsDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :goals="goals"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="fetchData"
    />
  </v-card>
</template>

<script>
import http from "@/services/http";
import EditGoalsDialog from "@/features/patients/profile-dashboard/dialogs/EditGoalsDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "GoalsDeadlinesCard",

  components: { EditGoalsDialog },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return {
      goals: [],
      dialogVisible: false,
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.goals || []).length;
    },
    hasData() {
      return this.goals.length > 0;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        const { data } = await http.get(`/api/patient-goals/${this.patientId}`);
        this.goals = data?.data || [];
      } catch (error) {
        console.error("Erro ao buscar metas:", error);
        this.goals = [];
      }
    },

    hasTarget(goal) {
      return goal.target_value !== null && goal.target_value !== undefined && goal.target_value !== "";
    },

    statusColor(status) {
      return (
        {
          "Concluído": "green darken-1",
          "Em Andamento": "blue darken-1",
          "Não Iniciado": "grey darken-1",
          "Cancelado": "red darken-1",
        }[status] || "grey"
      );
    },

    openDialog() {
      this.dialogVisible = true;
    },

  },
};
</script>

<style scoped>
/* ═══ Tema: Teal (Metas e Prazos) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #99f6e4 !important;
  box-shadow: 0 2px 10px rgba(13, 148, 136, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #5eead4 !important;
  box-shadow: 0 6px 20px rgba(13, 148, 136, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: var(--ds-soft-teal);
  border-bottom: 1px solid #99f6e4;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-cat-mint);
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #5eead4;
  border-radius: 10px;
}
.card-content::-webkit-scrollbar-thumb:hover {
  background: var(--ds-cat-mint);
}

.goals-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.goal-row {
  padding: 8px 12px;
  border-radius: 10px;
  background-color: #f0fdfa;
  border-left: 3px solid var(--ds-cat-mint);
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.goal-row:hover {
  background-color: #ccfbf1;
  transform: translateX(2px);
}

.goal-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.goal-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.goal-sub {
  font-size: 11px;
  color: var(--ds-text-600);
  margin-top: 2px;
}
</style>
